import { Radio } from 'antd';
import { Controller, useFormContext } from 'react-hook-form';
import { AppAdminFormLabel } from '@components/atoms';
import { TextFieldWithLabel } from '@components/molecules';
import { RadioGroupProps } from 'antd/lib';
import {
  useAdminBankPayment,
  useAdminBankType,
  useAdminBankBranchType,
  useAdminBankAccountType,
} from '@/hooks/admin';
import { useJapaneseNameKanaFields } from '@/hooks/useJapaneseNameKanaFields';
import { useBankNamePreview } from '@/hooks/useBankNamePreview';
import { ClinicCreateFormData } from '@/pages/admin/site-admin-clinic-info/clinicFormSchema';
import { useParams } from 'react-router-dom';

const ClinicPaymentInfoForm = () => {
  const { site } = useParams<{ site: string }>();
  const currentSite = site ?? 'medix';
  const { data: bankPaymentData } = useAdminBankPayment(currentSite);
  const { data: bankTypeData } = useAdminBankType(currentSite);
  const { data: bankBranchTypeData } = useAdminBankBranchType(currentSite);
  const { data: bankAccountTypeData } = useAdminBankAccountType(currentSite);

  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<ClinicCreateFormData>();

  const bankNameKana = watch('clinicPaymentInfo.bank_name_kana');
  const branchNameKana = watch('clinicPaymentInfo.bank_branch_name_kana');
  const accountNameKana = watch('clinicPaymentInfo.account_name_kana');

  const bankNameKanaFields = useJapaneseNameKanaFields({
    nameKanaValue: String(bankNameKana ?? ''),
    setNameValue: v =>
      setValue('clinicPaymentInfo.bank_name', v, { shouldDirty: true, shouldValidate: true }),
    setKanaValue: v =>
      setValue('clinicPaymentInfo.bank_name_kana', v, { shouldDirty: true, shouldValidate: true }),
    maxLength: 255,
    fullWidthAlphaNumOnBlur: false,
  });

  const branchNameKanaFields = useJapaneseNameKanaFields({
    nameKanaValue: String(branchNameKana ?? ''),
    setNameValue: v =>
      setValue('clinicPaymentInfo.bank_branch_name', v, { shouldDirty: true, shouldValidate: true }),
    setKanaValue: v =>
      setValue('clinicPaymentInfo.bank_branch_name_kana', v, {
        shouldDirty: true,
        shouldValidate: true,
      }),
    maxLength: 255,
    fullWidthAlphaNumOnBlur: false,
  });

  const accountNameKanaFields = useJapaneseNameKanaFields({
    nameKanaValue: String(accountNameKana ?? ''),
    setNameValue: v =>
      setValue('clinicPaymentInfo.account_name', v, { shouldDirty: true, shouldValidate: true }),
    setKanaValue: v =>
      setValue('clinicPaymentInfo.account_name_kana', v, { shouldDirty: true, shouldValidate: true }),
    maxLength: 255,
    fullWidthAlphaNumOnBlur: false,
  });

  const bankPaymentOptions: RadioGroupProps['options'] = (bankPaymentData?.data ?? []).map(
    ({ id, name }) => ({
      value: id,
      label: name,
    })
  );

  const bankTypeOptions: RadioGroupProps['options'] = bankTypeData?.data
    ? [
        { value: 0, label: 'なし' },
        ...bankTypeData.data.map(({ id, name }) => ({
          value: id,
          label: name,
        })),
      ]
    : [];

  const bankPreviewValue = useBankNamePreview<ClinicCreateFormData>({
    control,
    bankNameField: 'clinicPaymentInfo.bank_name',
    bankTypeField: 'clinicPaymentInfo.bank_type_id',
    bankNameSuffixField: 'clinicPaymentInfo.bank_name_suffix',
    bankTypeOptions,
    noneLabel: 'なし',
  });

  const bankBranchTypeOptions: RadioGroupProps['options'] = bankBranchTypeData?.data
    ? [
        { value: 0, label: 'なし' },
        ...bankBranchTypeData.data.map(({ id, name }) => ({
          value: id,
          label: name,
        })),
      ]
    : [];

  const bankAccountTypeOptions: RadioGroupProps['options'] = (bankAccountTypeData?.data ?? []).map(
    ({ id, name }) => ({
      value: id,
      label: name,
    })
  );

  const bankBranchPreviewValue = useBankNamePreview<ClinicCreateFormData>({
    control,
    bankNameField: 'clinicPaymentInfo.bank_branch_name',
    bankTypeField: 'clinicPaymentInfo.bank_branch_type_id',
    bankNameSuffixField: 'clinicPaymentInfo.branch_branch_name_suffix',
    bankTypeOptions: bankBranchTypeOptions,
    noneLabel: 'なし',
  });

  return (
    <div>
      <h3 className="text-xl my-4 font-bold">レセプト印字支払機関欄情報（個人請求用）</h3>
      <div className="mb-4">
        <AppAdminFormLabel label="支払方法区分" />
        <Controller
          name="clinicPaymentInfo.bank_payment_type_id"
          control={control}
          render={({ field }) => (
            <Radio.Group
              {...field}
              options={bankPaymentOptions}
              onChange={e => {
                field.onChange(e);
                setValue('clinicPaymentInfo.bank_payment_type_id', e.target.value, {
                  shouldValidate: true,
                });
              }}
            />
          )}
        />
      </div>

      <div className="grid grid-cols-4 gap-x-5 gap-y-4">
        <Controller
          name="clinicPaymentInfo.bank_code"
          control={control}
          render={({ field }) => (
            <TextFieldWithLabel
              label="金融機関コード"
              {...field}
              value={field.value ?? ''}
              error={errors.clinicPaymentInfo?.bank_code}
            />
          )}
        />
        <Controller
          name="clinicPaymentInfo.bank_name"
          control={control}
          render={({ field }) => (
            <TextFieldWithLabel
              label="銀行名"
              {...field}
              value={field.value ?? ''}
              {...bankNameKanaFields.nameInputProps}
              error={errors.clinicPaymentInfo?.bank_name}
            />
          )}
        />
        <Controller
          name="clinicPaymentInfo.bank_name_kana"
          control={control}
          render={({ field }) => (
            <TextFieldWithLabel
              label="銀行名カナ"
              {...field}
              value={field.value ?? ''}
              {...bankNameKanaFields.kanaInputProps}
              error={errors.clinicPaymentInfo?.bank_name_kana}
            />
          )}
        />
        <TextFieldWithLabel label="プレビュー" value={bankPreviewValue} disabled />
        <div>
          <AppAdminFormLabel label="銀行区分" />
          <Controller
            name="clinicPaymentInfo.bank_type_id"
            control={control}
            render={({ field }) => (
              <Radio.Group
                {...field}
                options={bankTypeOptions}
                onChange={e => {
                  field.onChange(e);
                  setValue('clinicPaymentInfo.bank_type_id', e.target.value, {
                    shouldValidate: true,
                  });
                }}
              />
            )}
          />
        </div>
        <Controller
          name="clinicPaymentInfo.bank_name_suffix"
          control={control}
          render={({ field }) => (
            <TextFieldWithLabel
              label="銀行名追加文言"
              {...field}
              value={field.value ?? ''}
              error={errors.clinicPaymentInfo?.bank_name_suffix}
            />
          )}
        />
        <div className="col-span-2" />

        <Controller
          name="clinicPaymentInfo.bank_branch_code"
          control={control}
          render={({ field }) => (
            <TextFieldWithLabel
              label="支店番号"
              {...field}
              value={field.value ?? ''}
              error={errors.clinicPaymentInfo?.bank_branch_code}
            />
          )}
        />
        <Controller
          name="clinicPaymentInfo.bank_branch_name"
          control={control}
          render={({ field }) => (
            <TextFieldWithLabel
              label="支店名"
              {...field}
              value={field.value ?? ''}
              {...branchNameKanaFields.nameInputProps}
              error={errors.clinicPaymentInfo?.bank_branch_name}
            />
          )}
        />
        <Controller
          name="clinicPaymentInfo.bank_branch_name_kana"
          control={control}
          render={({ field }) => (
            <TextFieldWithLabel
              label="支店名カナ"
              {...field}
              value={field.value ?? ''}
              {...branchNameKanaFields.kanaInputProps}
              error={errors.clinicPaymentInfo?.bank_branch_name_kana}
            />
          )}
        />
        <TextFieldWithLabel label="プレビュー" value={bankBranchPreviewValue} disabled />

        <div>
          <AppAdminFormLabel label="支店区分" />
          <Controller
            name="clinicPaymentInfo.bank_branch_type_id"
            control={control}
            render={({ field }) => (
              <Radio.Group
                {...field}
                options={bankBranchTypeOptions}
                onChange={e => {
                  field.onChange(e);
                  setValue('clinicPaymentInfo.bank_branch_type_id', e.target.value, {
                    shouldValidate: true,
                  });
                }}
              />
            )}
          />
        </div>
        <Controller
          name="clinicPaymentInfo.branch_branch_name_suffix"
          control={control}
          render={({ field }) => (
            <TextFieldWithLabel
              label="支店名追加文言"
              {...field}
              value={field.value ?? ''}
              error={errors.clinicPaymentInfo?.branch_branch_name_suffix}
            />
          )}
        />
        <div className="col-span-2" />

        <div>
          <AppAdminFormLabel label="預金の種類" />
          <Controller
            name="clinicPaymentInfo.bank_account_type_id"
            control={control}
            render={({ field }) => (
              <Radio.Group
                {...field}
                options={bankAccountTypeOptions}
                onChange={e => {
                  field.onChange(e);
                  setValue('clinicPaymentInfo.bank_account_type_id', e.target.value, {
                    shouldValidate: true,
                  });
                }}
              />
            )}
          />
        </div>
        <Controller
          name="clinicPaymentInfo.account_number"
          control={control}
          render={({ field }) => (
            <TextFieldWithLabel
              label="口座番号"
              {...field}
              value={field.value ?? ''}
              error={errors.clinicPaymentInfo?.account_number}
            />
          )}
        />
        <Controller
          name="clinicPaymentInfo.account_name"
          control={control}
          render={({ field }) => (
            <TextFieldWithLabel
              label="口座名義"
              {...field}
              value={field.value ?? ''}
              {...accountNameKanaFields.nameInputProps}
              error={errors.clinicPaymentInfo?.account_name}
            />
          )}
        />
        <Controller
          name="clinicPaymentInfo.account_name_kana"
          control={control}
          render={({ field }) => (
            <TextFieldWithLabel
              label="口座名義カナ"
              {...field}
              value={field.value ?? ''}
              {...accountNameKanaFields.kanaInputProps}
              error={errors.clinicPaymentInfo?.account_name_kana}
            />
          )}
        />
      </div>
    </div>
  );
};

export default ClinicPaymentInfoForm;
